import { Link } from 'react-router-dom';
import { useState } from 'react';
import { FileSpreadsheet, FileText } from 'lucide-react';
import { Topbar } from '@/components/layout/Topbar';
import { useEtatBalance, useEtatBalanceAgee, useEtatGrandLivre } from '@/hooks/useJournal';
import { useAuthStore } from '@/stores/authStore';
import { formatNumber } from '@/utils/currency';
import { buildExportFilename, exportToPDF, exportToXLSX, type ExportColumn } from '@/utils/exports';

/** Montant en FCFA depuis des centimes sérialisés. */
const F = (c: string | number | bigint | null | undefined) => (c == null ? '' : formatNumber(Number(BigInt(c)) / 100));
const N = (c: string) => Number(BigInt(c)) / 100;   // pour l'export Excel (nombre)
const debutAnnee = () => `${new Date().getFullYear()}-01-01`;
const aujourdhui = () => new Date().toISOString().slice(0, 10);
const TRANCHES = [['a_jour', 'À jour'], ['j_0_30', '0–30 j'], ['j_30_60', '30–60 j'], ['j_60_90', '60–90 j'], ['plus_90', '+ 90 j']] as const;
const CHAMP = { display: 'grid', gap: 4, fontSize: 12, color: 'var(--tx-2)' } as const;

type Onglet = 'balance' | 'grand-livre' | 'agee';

/**
 * États comptables — lot 2, étape 2.8. Tout vient du grand livre (écritures VALIDÉES ; brouillons exclus et signalés).
 * Balance générale, grand livre, balance âgée clients / fournisseurs ; export Excel et PDF.
 */
export const EtatsComptablesPage = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const [onglet, setOnglet] = useState<Onglet>('balance');
  const [du, setDu] = useState(debutAnnee());
  const [au, setAu] = useState(aujourdhui());
  const [compte, setCompte] = useState('');
  const [typeAgee, setTypeAgee] = useState<'clients' | 'fournisseurs'>('clients');
  const balance = useEtatBalance(du, au);
  const grandLivre = useEtatGrandLivre({ du, au, compte: compte || undefined }, onglet === 'grand-livre');
  const agee = useEtatBalanceAgee(typeAgee, au);
  const nom = filiale?.name ?? '';

  const exporter = async (format: 'xlsx' | 'pdf') => {
    if (onglet === 'balance' && balance.data) {
      type R = (typeof balance.data.comptes)[number];
      const cols: ExportColumn<R>[] = [
        { header: 'Compte', accessor: (r) => r.code }, { header: 'Libellé', accessor: (r) => r.libelle },
        { header: 'Ouverture débit', accessor: (r) => N(r.ouvertureDebit), align: 'right' }, { header: 'Ouverture crédit', accessor: (r) => N(r.ouvertureCredit), align: 'right' },
        { header: 'Mouvements débit', accessor: (r) => N(r.mouvementDebit), align: 'right' }, { header: 'Mouvements crédit', accessor: (r) => N(r.mouvementCredit), align: 'right' },
        { header: 'Solde débiteur', accessor: (r) => N(r.soldeDebit), align: 'right' }, { header: 'Solde créditeur', accessor: (r) => N(r.soldeCredit), align: 'right' },
      ];
      const t = balance.data.totaux;
      const rows = [...balance.data.comptes, { code: 'TOTAL', libelle: '', classe: '', ...t } as R];
      const filename = buildExportFilename('balance-generale', filiale?.filialeKey, format);
      if (format === 'xlsx') await exportToXLSX({ filename, columns: cols, rows, sheetName: 'Balance' });
      else await exportToPDF({ filename, columns: cols, rows, title: `Balance générale — ${nom}`, subtitle: `Du ${du} au ${au}`, orientation: 'landscape' });
    }
    if (onglet === 'grand-livre' && grandLivre.data) {
      type R = { compte: string; date: string; piece: string; journal: string; libelle: string; tiers: string; lettrage: string; debit: number; credit: number; solde: number };
      const rows: R[] = grandLivre.data.comptes.flatMap((c) => [
        { compte: `${c.code} ${c.libelle}`, date: du, piece: '', journal: '', libelle: 'Solde d’ouverture', tiers: '', lettrage: '', debit: 0, credit: 0, solde: N(c.ouverture) },
        ...c.lignes.map((l) => ({ compte: c.code, date: l.date, piece: l.numeroPiece ?? '', journal: l.journal ?? '', libelle: l.libelle,
                                  tiers: l.tiers ?? '', lettrage: l.lettrage ?? '', debit: N(l.debit), credit: N(l.credit), solde: N(l.solde) })),
      ]);
      const cols: ExportColumn<R>[] = [
        { header: 'Compte', accessor: (r) => r.compte }, { header: 'Date', accessor: (r) => r.date }, { header: 'N° pièce', accessor: (r) => r.piece },
        { header: 'Journal', accessor: (r) => r.journal }, { header: 'Libellé', accessor: (r) => r.libelle }, { header: 'Tiers', accessor: (r) => r.tiers },
        { header: 'Lettrage', accessor: (r) => r.lettrage }, { header: 'Débit', accessor: (r) => r.debit, align: 'right' },
        { header: 'Crédit', accessor: (r) => r.credit, align: 'right' }, { header: 'Solde', accessor: (r) => r.solde, align: 'right' },
      ];
      const filename = buildExportFilename('grand-livre', filiale?.filialeKey, format);
      if (format === 'xlsx') await exportToXLSX({ filename, columns: cols, rows, sheetName: 'Grand livre' });
      else await exportToPDF({ filename, columns: cols, rows, title: `Grand livre — ${nom}`, subtitle: `Du ${du} au ${au}${compte ? ` · compte ${compte}` : ''}`, orientation: 'landscape' });
    }
    if (onglet === 'agee' && agee.data) {
      type R = (typeof agee.data.parTiers)[number];
      const cols: ExportColumn<R>[] = [
        { header: typeAgee === 'clients' ? 'Client' : 'Fournisseur', accessor: (r) => r.nom }, { header: 'Documents', accessor: (r) => r.nb },
        ...TRANCHES.map(([k, l]) => ({ header: l, accessor: (r: R) => N(r[k]), align: 'right' as const })),
        { header: 'Total', accessor: (r) => N(r.total), align: 'right' },
      ];
      const filename = buildExportFilename(`balance-agee-${typeAgee}`, filiale?.filialeKey, format);
      if (format === 'xlsx') await exportToXLSX({ filename, columns: cols, rows: agee.data.parTiers, sheetName: 'Balance âgée' });
      else await exportToPDF({ filename, columns: cols, rows: agee.data.parTiers, title: `Balance âgée ${typeAgee} — ${nom}`, subtitle: `Au ${au}`, orientation: 'landscape' });
    }
  };

  const brouillons = onglet === 'balance' ? balance.data?.brouillonsExclus : onglet === 'grand-livre' ? grandLivre.data?.brouillonsExclus : 0;

  return (
    <>
      <Topbar
        title="États comptables"
        subtitle="Balance générale, grand livre, balance âgée — écritures validées"
        actions={
          <>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void exporter('xlsx')}><FileSpreadsheet size={14} /> Excel</button>
            <button className="ds-btn ds-btn-g ds-btn-sm" onClick={() => void exporter('pdf')}><FileText size={14} /> PDF</button>
          </>
        }
      />
      <div className="ds-content">
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {([['balance', 'Balance générale'], ['grand-livre', 'Grand livre'], ['agee', 'Balance âgée']] as const).map(([k, l]) => (
            <button key={k} type="button" className={`ds-btn ds-btn-sm ${onglet === k ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setOnglet(k)}>{l}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, marginBottom: 12, flexWrap: 'wrap', alignItems: 'end' }}>
          {onglet !== 'agee' && <label style={CHAMP}>Du<input type="date" value={du} onChange={(e) => setDu(e.target.value)} /></label>}
          <label style={CHAMP}>{onglet === 'agee' ? 'Au' : 'Au'}<input type="date" value={au} onChange={(e) => setAu(e.target.value)} /></label>
          {onglet === 'grand-livre' && <label style={CHAMP}>Compte (6 chiffres, vide = tous)<input value={compte} onChange={(e) => setCompte(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="411000" /></label>}
          {onglet === 'agee' && (
            <div style={{ display: 'flex', gap: 6 }}>
              {(['clients', 'fournisseurs'] as const).map((t) => (
                <button key={t} type="button" className={`ds-btn ds-btn-sm ${typeAgee === t ? 'ds-btn-p' : 'ds-btn-g'}`} onClick={() => setTypeAgee(t)}>{t === 'clients' ? 'Clients' : 'Fournisseurs'}</button>
              ))}
            </div>
          )}
        </div>
        {!!brouillons && (
          <div className="ds-alert-strip" style={{ marginBottom: 12 }}><div className="ds-alert-txt">{brouillons} écriture(s) en brouillon non comprise(s) : à valider pour qu'elles apparaissent.</div></div>
        )}

        {onglet === 'balance' && (
          <div className="ds-table-panel">
            <table className="ds-tbl" style={{ width: '100%' }}>
              <thead><tr>
                <th>Compte</th><th>Libellé</th><th style={{ textAlign: 'right' }}>Ouverture D</th><th style={{ textAlign: 'right' }}>Ouverture C</th>
                <th style={{ textAlign: 'right' }}>Mouvements D</th><th style={{ textAlign: 'right' }}>Mouvements C</th>
                <th style={{ textAlign: 'right' }}>Solde D</th><th style={{ textAlign: 'right' }}>Solde C</th>
              </tr></thead>
              <tbody>
                {balance.isLoading && <tr><td colSpan={8} style={{ padding: 16 }}>Chargement…</td></tr>}
                {balance.data?.comptes.map((c) => (
                  <tr key={c.code}>
                    <td className="ds-compte-code">{c.code}</td><td style={{ fontSize: 12 }}>{c.libelle}</td>
                    <td className="ds-am">{F(c.ouvertureDebit)}</td><td className="ds-am">{F(c.ouvertureCredit)}</td>
                    <td className="ds-am">{F(c.mouvementDebit)}</td><td className="ds-am">{F(c.mouvementCredit)}</td>
                    <td className="ds-am">{F(c.soldeDebit)}</td><td className="ds-am">{F(c.soldeCredit)}</td>
                  </tr>
                ))}
              </tbody>
              {balance.data && (
                <tfoot><tr>
                  <td colSpan={2}><b>Total</b> · {balance.data.equilibree ? '✓ débit = crédit' : '⚠ déséquilibre'}</td>
                  <td className="ds-am">{F(balance.data.totaux.ouvertureDebit)}</td><td className="ds-am">{F(balance.data.totaux.ouvertureCredit)}</td>
                  <td className="ds-am">{F(balance.data.totaux.mouvementDebit)}</td><td className="ds-am">{F(balance.data.totaux.mouvementCredit)}</td>
                  <td className="ds-am">{F(balance.data.totaux.soldeDebit)}</td><td className="ds-am">{F(balance.data.totaux.soldeCredit)}</td>
                </tr></tfoot>
              )}
            </table>
          </div>
        )}

        {onglet === 'grand-livre' && (
          <div style={{ display: 'grid', gap: 14 }}>
            {grandLivre.isLoading && <div>Chargement…</div>}
            {grandLivre.data?.comptes.length === 0 && <div style={{ color: 'var(--tx-3)' }}>Aucune écriture validée sur la période.</div>}
            {grandLivre.data?.comptes.map((c) => (
              <div key={c.code} className="ds-table-panel">
                <div style={{ padding: '10px 12px', fontWeight: 700 }}>{c.code} · {c.libelle}</div>
                <table className="ds-tbl" style={{ width: '100%' }}>
                  <thead><tr>
                    <th>Date</th><th>N° pièce</th><th>Jnl</th><th>Libellé</th><th>Tiers</th><th>Lettrage</th>
                    <th style={{ textAlign: 'right' }}>Débit</th><th style={{ textAlign: 'right' }}>Crédit</th><th style={{ textAlign: 'right' }}>Solde</th>
                  </tr></thead>
                  <tbody>
                    <tr><td colSpan={8} style={{ fontSize: 12, color: 'var(--tx-3)' }}>Solde d'ouverture au {grandLivre.data!.du}</td><td className="ds-am">{F(c.ouverture)}</td></tr>
                    {c.lignes.map((l, i) => (
                      <tr key={i}>
                        <td style={{ fontSize: 12, whiteSpace: 'nowrap' }}>{l.date}</td><td><span className="ds-ref-code">{l.numeroPiece}</span></td>
                        <td style={{ fontSize: 12 }}>{l.journal}</td><td style={{ fontSize: 12 }}>{l.libelle}</td>
                        <td style={{ fontSize: 12 }}>{l.tiers ?? ''}</td><td style={{ fontSize: 12 }}>{l.lettrage ?? ''}</td>
                        <td className="ds-am">{BigInt(l.debit) > 0n ? F(l.debit) : ''}</td><td className="ds-am">{BigInt(l.credit) > 0n ? F(l.credit) : ''}</td>
                        <td className="ds-am">{F(l.solde)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot><tr><td colSpan={6}>Totaux de la période</td><td className="ds-am">{F(c.debit)}</td><td className="ds-am">{F(c.credit)}</td><td className="ds-am">{F(c.solde)}</td></tr></tfoot>
                </table>
              </div>
            ))}
          </div>
        )}

        {onglet === 'agee' && (
          <div className="ds-table-panel">
            <table className="ds-tbl" style={{ width: '100%' }}>
              <thead><tr>
                <th>{typeAgee === 'clients' ? 'Client' : 'Fournisseur'}</th><th>Documents</th>
                {TRANCHES.map(([k, l]) => <th key={k} style={{ textAlign: 'right' }}>{l}</th>)}<th style={{ textAlign: 'right' }}>Total</th>
              </tr></thead>
              <tbody>
                {agee.isLoading && <tr><td colSpan={8} style={{ padding: 16 }}>Chargement…</td></tr>}
                {agee.data?.parTiers.length === 0 && <tr><td colSpan={8} style={{ padding: 16, color: 'var(--tx-3)' }}>Rien à recouvrer / à payer.</td></tr>}
                {agee.data?.parTiers.map((t) => (
                  <tr key={t.contactId}>
                    <td style={{ fontSize: 12 }}><Link to={`/finance/releves/${t.contactId}`} title="Relevé de compte" style={{ color: 'var(--p)', textDecoration: 'underline' }}>{t.nom}</Link></td><td style={{ fontSize: 12 }}>{t.nb}</td>
                    {TRANCHES.map(([k]) => <td key={k} className="ds-am">{BigInt(t[k]) !== 0n ? F(t[k]) : ''}</td>)}
                    <td className="ds-am"><b>{F(t.total)}</b></td>
                  </tr>
                ))}
              </tbody>
              {agee.data && (
                <tfoot><tr>
                  <td colSpan={2}><b>Total</b></td>
                  {TRANCHES.map(([k]) => <td key={k} className="ds-am">{F(agee.data!.tranches[k])}</td>)}
                  <td className="ds-am"><b>{F(agee.data.total)}</b></td>
                </tr></tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </>
  );
};
